/**
 * ecount-sync-api — 실사 탭(이카운트 동기화)의 API.
 *
 * 백엔드 `/api/admin/ecount-sync` (hcltech-parts `EcountSync.Routes.ts` · `EcountSync.Types.ts`).
 * 응답 키는 DB 열 그대로(snake_case). VOCABULARY "이카운트 동기화 — 실사 탭".
 *
 * 1 타입 → 2 시그니처 → 3 몸통(이 커밋).
 */
import { z } from 'zod';
import { downloadFile, getJson, postForm, postJson, putJson } from './http';

// ─── 열거 — 백엔드와 같은 철자 ───────────────────────────────────────
/** 자료 — 올릴 수 있는 셋. 백엔드 E_ECOUNT_SHEETS 엔 'serial' 이 더 있지만 아직 못 올린다 */
export const E_ECOUNT_SHEETS = ['item', 'stock', 'purchase'] as const;
export type  EEcountSheet    = typeof E_ECOUNT_SHEETS[number];

/** 판정 넷 — 새것 · 같음 · 다름 · 빠짐 */
export const E_ROW_VERDICTS = ['new', 'same', 'changed', 'missing'] as const;
export type  ERowVerdict    = typeof E_ROW_VERDICTS[number];

/** 연결의 종류 — pn 코드가 이음 · manual 사람이 이음 · none 못 이음 */
export const E_MATCH_KINDS = ['pn', 'manual', 'none'] as const;
export type  EMatchKind    = typeof E_MATCH_KINDS[number];

/** 품목 줄의 할 일 — 그대로 · 잇기 · 만들기 · 연결 안 됨 */
export const E_LINK_PLANS = ['keep', 'link', 'create', 'unmapped'] as const;
export type  ELinkPlan    = typeof E_LINK_PLANS[number];

/** 재고 줄의 경고 — 음수 · 못 뺌 · 연결 안 됨 */
export const E_STOCK_WARNINGS = ['negative', 'short', 'unlinked'] as const;
export type  EStockWarning    = typeof E_STOCK_WARNINGS[number];

// ─── 스키마 ─────────────────────────────────────────────────────────
const ProductRefSchema = z.object({ id: z.string(), hclt_number: z.string() });

/** 품목등록 미리보기 한 줄 */
export const ItemPreviewLineSchema = z.object({
  item_code:       z.string(),
  verdict:         z.enum(E_ROW_VERDICTS),
  changed_columns: z.array(z.string()),                 // 다름 줄의 바뀐 열 이름(품목 값 열 열 개 중)
  part_number:     z.string(),                          // '' = 없음
  product:         ProductRefSchema.nullable(),         // 이어질(이어진) product
  link:            z.enum(E_LINK_PLANS),
});

/** 재고현황 미리보기 한 줄 — missing 은 이 자료에 없어 재고 0 이 되는 품목 */
export const StockPreviewLineSchema = z.object({
  item_code: z.string(),
  verdict:   z.enum(E_ROW_VERDICTS),
  quantity:  z.number().int(),                          // 이카운트 재고(음수 그대로)
  held:      z.number().int(),                          // 우리 보유 실물 수
  add:       z.number().int(),
  remove:    z.number().int(),
  link:      z.enum(E_MATCH_KINDS).nullable(),          // null = 연결 줄 없음
  warning:   z.enum(E_STOCK_WARNINGS).nullable(),
});

/** 구매현황 미리보기 한 줄 */
export const PurchasePreviewLineSchema = z.object({
  slip_no: z.string(),
  line_seq:   z.number().int(),
  item_code:  z.string(),
  verdict:    z.enum(E_ROW_VERDICTS),
  unit_price: z.string().nullable(),                    // NUMERIC 글. null = 내부 입고
});

const PreviewHeadSchema   = z.object({ upload_id: z.string(), exported_at: z.string() });
const VerdictCountsSchema = z.object({ new: z.number().int(), same: z.number().int(), changed: z.number().int(), missing: z.number().int() });

/** 미리보기 — 자료로 갈린다(백엔드 SyncPreview 합집합) */
export const SyncPreviewSchema = z.discriminatedUnion('sheet', [
  PreviewHeadSchema.extend({
    sheet:  z.literal('item'),
    counts: VerdictCountsSchema.extend({ unmapped: z.number().int() }),
    lines:  z.array(ItemPreviewLineSchema),
  }),
  PreviewHeadSchema.extend({
    sheet:  z.literal('stock'),
    counts: VerdictCountsSchema.extend({ add_units: z.number().int(), remove_units: z.number().int(), warnings: z.number().int() }),
    lines:  z.array(StockPreviewLineSchema),
  }),
  PreviewHeadSchema.extend({
    sheet:  z.literal('purchase'),
    counts: VerdictCountsSchema.extend({ units_costed: z.number().int(), units_without_cost: z.number().int() }),
    lines:  z.array(PurchasePreviewLineSchema),
  }),
]);

/** 반영 한 번의 보고 — 자료마다 안 쓰는 칸은 0 */
export const SyncReportSchema = z.object({
  upload_id:        z.string(),
  inserted:         z.number().int(),
  updated:          z.number().int(),
  skipped:          z.number().int(),
  unmapped:         z.number().int(),
  products_created: z.number().int(),
  products_linked:  z.number().int(),
  units_added:      z.number().int(),
  units_retired:    z.number().int(),
  units_costed:     z.number().int(),
});

/** 업로드 한 건 — 백엔드 ECOUNT_UPLOAD_VIEW_COLUMNS */
export const EcountUploadViewSchema = z.object({
  id:          z.string(),
  sheet:       z.enum([...E_ECOUNT_SHEETS, 'serial']),   // 표의 CHECK 넷 — 목록엔 올 수 있다
  exported_at: z.string(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  applied_at:  z.string().nullable(),                     // null = 미리보기만
  row_count:   z.number().int(),
  inserted:    z.number().int().nullable(),               // 반영 전엔 null
  updated:     z.number().int().nullable(),
  skipped:     z.number().int().nullable(),
  unmapped:    z.number().int().nullable(),
});
export const EcountUploadViewListSchema = z.object({
  rows:   z.array(EcountUploadViewSchema),
  total:  z.number().int(),
  limit:  z.number().int(),
  offset: z.number().int(),
});

/** 연결 안 된 품목 한 줄 — 사람이 잇는 재료 */
export const UnmappedItemViewSchema = z.object({
  item_code:      z.string(),
  item_name:      z.string(),
  part_number:    z.string(),
  description:    z.string(),
  condition_name: z.string(),
  stock_quantity: z.number().int().nullable(),             // 이카운트 재고. 재고현황에 없으면 null — 재고 있는 품목부터
});
export const UnmappedItemsResponseSchema = z.object({ rows: z.array(UnmappedItemViewSchema) });

/** 잇기의 답 — PUT /items/:itemCode/product */
export const EcountItemLinkSchema = z.object({ item_code: z.string(), hclt_product_id: z.string() });

export type ItemPreviewLine      = z.infer<typeof ItemPreviewLineSchema>;
export type StockPreviewLine     = z.infer<typeof StockPreviewLineSchema>;
export type PurchasePreviewLine  = z.infer<typeof PurchasePreviewLineSchema>;
export type SyncPreview          = z.infer<typeof SyncPreviewSchema>;
export type SyncReport           = z.infer<typeof SyncReportSchema>;
export type EcountUploadView     = z.infer<typeof EcountUploadViewSchema>;
export type EcountUploadViewList = z.infer<typeof EcountUploadViewListSchema>;
export type UnmappedItemView     = z.infer<typeof UnmappedItemViewSchema>;
export type EcountItemLink       = z.infer<typeof EcountItemLinkSchema>;

/** 올리기 입력 — purchase 만 slipFile 필수. 파일은 브라우저 File 그대로 multipart 로 */
export type EcountUploadInput = { sheet: EEcountSheet; exportedAt: string; file: File; slipFile: File | null };

// ─── 함수 — 백엔드 /api/admin/ecount-sync ───────────────────────────
const BASE = '/api/admin/ecount-sync';

/** 올리기 — multipart(sheet · exported_at · file · slip_file). 아무것도 안 쓴다 — 업로드 한 건과 미리보기만. 순서 어긋남은 서버의 409 글 그대로 */
export async function uploadEcountSheet(input: EcountUploadInput): Promise<SyncPreview> {
  const form = new FormData();
  form.set('sheet', input.sheet);
  form.set('exported_at', input.exportedAt);
  form.set('file', input.file);
  if (input.slipFile !== null) form.set('slip_file', input.slipFile);
  return postForm(`${BASE}/uploads`, form, SyncPreviewSchema);
}

/** 업로드 내역 — 최근 먼저 */
export async function fetchEcountUploads(limit: number, offset: number): Promise<EcountUploadViewList> {
  return getJson(`${BASE}/uploads?limit=${limit}&offset=${offset}`, { schema: EcountUploadViewListSchema });
}

/** 업로드 하나의 미리보기 다시 보기 — 반영된 것도 보인다(전부 같음이 정상) */
export async function fetchSyncPreview(uploadId: string): Promise<SyncPreview> {
  return getJson(`${BASE}/uploads/${encodeURIComponent(uploadId)}`, { schema: SyncPreviewSchema });
}

/** 반영 — 한 번만. 반영된 업로드는 409 */
export async function applyEcountSync(uploadId: string): Promise<SyncReport> {
  return postJson(`${BASE}/uploads/${encodeURIComponent(uploadId)}/apply`, {}, SyncReportSchema);
}

/** 연결 안 된 품목 */
export async function fetchUnlinkedEcountItems(): Promise<UnmappedItemView[]> {
  const body = await getJson(`${BASE}/unmapped`, { schema: UnmappedItemsResponseSchema });
  return body.rows;
}

/** 빈 단가 줄 CSV — 반영된 구매 표의 단가 없는 줄. 제품팀이 이카운트에서 채울 목록. 파일명은 서버와 같은 empty-price-<오늘>.csv */
export async function downloadEmptyPricePurchasesCsv(): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  await downloadFile(`${BASE}/purchases/empty-price.csv`, `empty-price-${today}.csv`);
}

/** 잇기 — 사람이 product 에(manual). 칸 하나라 set */
export async function setEcountItemProduct(itemCode: string, productId: string): Promise<EcountItemLink> {
  return putJson(`${BASE}/items/${encodeURIComponent(itemCode)}/product`, { hclt_product_id: productId }, EcountItemLinkSchema);
}
